import React, { useState, useRef, useEffect } from 'react'

import { BarChartContainer } from 'nswidgets/src/components/bar/bar'
import { mapFromRawData } from 'nswidgets/src/components/table/table-mapping'
import { ChartFlipper } from '../chart-flipper'
import { chartFlipperService } from '../chart-flipper.service'
import { ControlBar } from '../control-bar'
import { blockDataService } from '../block-data.service'
import { barBlockService } from './bar-block.service'
import { ChartLoader } from '../chart-loader'
import { reportLayoutService } from '../report-layout.service'

import { useStyles } from './bar-block.style'
import { Legend } from 'nswidgets/src/common/legend/legend'
import { SplitChartFilterBar } from '../split-chart-filter-bar'
import { BarBlockChart } from './bar-block-chart'
import { SideBySideChart } from '../side-by-side-chart'
import { reportDataSetService } from '../../editor/report-data-set.service'
import { SideBySideChartContainer } from '../side-by-side-chart-container'

const DEFAULT_WIDTH = 1024

export const BarBlock = function ({
  blockKey,
  dataKey,
  report,
  splitChart,
  strataDefinition,
  sliderDefinition,
  filterDefinition,
  tableDefinition,
  parentControlBar,
  parentBlock,
  parentBlockSelections,
  width = DEFAULT_WIDTH,
  axisLabels = {},
  ...props
}) {
  const classes = useStyles()
  const rawData = useRef([])
  const legendToggles = useRef()
  const height = width / 2

  const [datasets, setDatasets] = useState([])
  const [tableDatasets, setTableDatasets] = useState([])

  const [selections, setSelections] = useState()
  const [splitChartSelections, setSplitChartSelections] = useState(
    reportDataSetService.getFilterDefinitionSplitChartSelections(filterDefinition)
  )
  const [labels, setLabels] = useState()
  const [propOverrides, setPropOverrides] = useState()

  const [showTable, setShowTable] = useState(false)
  const [selectedTableRowKey, setSelectedTableRowKey] = useState()

  if (!axisLabels.x) {
    axisLabels.x = ''
  }
  if (!axisLabels.y) {
    axisLabels.y = ''
  }

  props = initializeProps(props, propOverrides)

  tableDefinition = chartFlipperService.applyTableDefinitionDefaults(tableDefinition, {
    primaryLabel: (props.axisLabels || {}).x,
    valueField: props.dataDefinition.yField,
    valueFormat: props.yFormat
  })

  useEffect(() => {
    updateChart(selections)
  }, [strataDefinition, props.yFormat])

  function initializeProps (props, propOverrides) {
    // Add default prop values
    props = {
      dataDefinition: { emptyValue: 'NA', xField: 'x', yField: 'n', groupField: 'group' },
      ...props
    }

    // Apply any prop overrides
    return reportLayoutService.applyPropOverrides(props, propOverrides)
  }

  function onDataLoad (data) {
    let dataList = splitChart ? data : [data]
    rawData.current.length = dataList.length
    dataList.forEach((data, i) => {
      rawData.current[i] = data
    })
    legendToggles.current = {}
    updateChart(selections)
  }

  function updateChart (currentSelections) {
    if (!rawData || !rawData.current[0] || (splitChart && !rawData.current[1])) {
      return
    }

    const labels = barBlockService.getLabels(strataDefinition, selections && selections.strata)
    setLabels(labels)

    const newChartDatasets = []
    const newTableDatasets = []
    rawData.current.forEach((rawData, i) => {
      const filteredData = blockDataService.filterBlockDataByKeyValue(rawData, sliderDefinition && sliderDefinition.key, currentSelections.slider)
      newChartDatasets.push({
        data: filteredData,
        groupField: (selections && selections.strata !== props.dataDefinition.emptyValue) ? selections.strata : props.dataDefinition.groupField
      })
      if (tableDefinition) {
        const tableData = barBlockService.transformToTableData(filteredData, props.dataDefinition, selections, {
          columnsToHide: legendToggles.current,
          labels
        })
        const cellFormats = {
          default: tableDefinition.cellFormat
        }
        const primaryLabel = (props && props.axisLabels) ? props.axisLabels.x : ''
        newTableDatasets.push(mapFromRawData(tableData, { cellFormats, primaryLabel }))
      }
    })

    setDatasets(newChartDatasets)
    setTableDatasets(newTableDatasets)
  }

  function onControlBarChange (selections, changes) {
    if (changes.tableToggle) {
      setSelectedTableRowKey(undefined)
      setShowTable(selections.tableToggle.on)
      return // table toggle change will always be alone
    }

    if (changes.props) {
      setPropOverrides(selections.props)
    }

    if (changes.slider) {
      updateChart(selections)
    } else {
      setSelections(selections)
    }
  }

  function onLegendToggle (toggledLegends) {
    if (!tableDatasets || !tableDatasets.length) {
      return
    }

    legendToggles.current = toggledLegends
    updateChart(selections)
  }

  function onBarClick ({ indexValue } = {}) {
    if (report.isInteractive && tableDefinition) {
      if (indexValue !== undefined) {
        setSelectedTableRowKey(indexValue)
      } else {
        setSelectedTableRowKey()
      }
      setShowTable(true)
    }
  }

  const barChartProps = {
    ...props,
    xField: props.dataDefinition.xField,
    yField: props.dataDefinition.yField,
    groupField: !!datasets && !!datasets.length && datasets[0].groupField,
    defaultLegendToggles: legendToggles.current,
    onLegendToggle,
    onClick: onBarClick,
    labels,
    width: splitChart ? width / 2 : width,
    height: splitChart ? height / 1.25 : height
  }

  const missingSideBySideChartStyle = {
    width: 500,
    height: 335,
    marginTop: 12
  }

  const tableProps = (tableDefinition || {}).props || {}

  if (splitChart) {
    tableProps.width = width / 2 - 16
    barChartProps.marginTop = 0
  }

  return (
    <div className="report-full-width">
      <ControlBar
        strataDefinition={strataDefinition}
        sliderDefinition={sliderDefinition}
        filterDefinition={filterDefinition}
        parentControlBar={parentControlBar}
        parentBlock={parentBlock}
        parentBlockSelections={parentBlockSelections}
        tableToggle={{ enabled: true, on: showTable }}
        onChange={onControlBarChange} />
      <ChartLoader report={report} blockKey={blockKey} dataKey={dataKey} sliderDefinition={sliderDefinition} selections={selections} splitChart={splitChart} splitChartSelections={splitChartSelections} width={width} initialHeight={height} onDataLoad={onDataLoad}>
        {datasets && tableDatasets && datasets.length && tableDatasets.length ? (
          <BarChartContainer datasets={datasets.map((d) => d.data)} {...barChartProps}>
            {({ colors, chartDatasets, legendToggles, legendLabels, onLegendToggle, containerRef }) => {
              return (
                <>
                  {splitChart && <Legend labels={legendLabels || []} colors={colors} toggleDefaults={legendToggles || {}} onToggle={onLegendToggle} />}
                  {splitChart && <SplitChartFilterBar filterDefinition={filterDefinition} onChange={setSplitChartSelections} className={classes.sideBySideChartFilterBar} />}
                  {splitChart && chartDatasets && chartDatasets.length === 2 ? (
                    <SideBySideChartContainer>
                      <SideBySideChart
                        className={classes.sideBySideChart}
                        data={chartDatasets && chartDatasets[0]}
                        missingChartStyles={missingSideBySideChartStyle}
                      >
                        <ChartFlipper
                          isHalf
                          showTable={showTable}
                          dataKey={dataKey}
                          tableData={tableDatasets[0]}
                          tableProps={tableProps}
                          selectedTableRowKey={selectedTableRowKey}
                          tableStyles={{ marginTop: 12, transform: 'translateX(18px)' }}
                          tableOffset={12}
                        >
                          <div className={tableDefinition ? classes.pointer : ''} data-qa='bar-chart-view'>
                            <BarBlockChart
                              containerRef={containerRef}
                              axisLabels={axisLabels}
                              chartData={chartDatasets && chartDatasets.length ? chartDatasets[0] : {}}
                              legendToggles={legendToggles}
                              {...barChartProps}
                            />
                          </div>
                        </ChartFlipper>
                      </SideBySideChart>
                      <SideBySideChart
                        className={classes.sideBySideChart}
                        data={chartDatasets && chartDatasets[1]}
                        missingChartStyles={{ ...missingSideBySideChartStyle, marginLeft: 22 }}
                      >
                        <ChartFlipper
                          isHalf
                          showTable={showTable}
                          dataKey={dataKey}
                          tableData={tableDatasets[1]}
                          tableProps={(tableDefinition || {}).props}
                          selectedTableRowKey={selectedTableRowKey}
                          tableStyles={{ marginTop: 12, marginLeft: 16 }}
                          tableOffset={12}
                        >
                          <div className={tableDefinition ? classes.pointer : ''} data-qa='bar-chart-view'>
                            <BarBlockChart
                              containerRef={containerRef}
                              axisLabels={axisLabels}
                              hiddenAxisLabels={['y']}
                              chartData={chartDatasets && chartDatasets.length ? chartDatasets[1] : {}}
                              legendToggles={legendToggles}
                              {...barChartProps}
                            />
                          </div>
                        </ChartFlipper>
                      </SideBySideChart>
                    </SideBySideChartContainer>
                  ) : (
                    <ChartFlipper
                      showTable={showTable}
                      dataKey={dataKey}
                      tableData={tableDatasets[0]}
                      tableProps={tableProps}
                      selectedTableRowKey={selectedTableRowKey}
                    >
                      <div className={tableDefinition ? classes.pointer : ''} data-qa='bar-chart-view'>
                        <Legend labels={legendLabels || []} colors={colors} toggleDefaults={legendToggles || {}} onToggle={onLegendToggle} />
                        {chartDatasets && (
                          <BarBlockChart
                            containerRef={containerRef}
                            axisLabels={axisLabels}
                            chartData={chartDatasets && chartDatasets.length ? chartDatasets[0] : {}}
                            legendToggles={legendToggles}
                            {...barChartProps}
                          />
                        )}
                      </div>
                    </ChartFlipper>
                  )}
                </>
              )
            }}
          </BarChartContainer>
        ) : null}
      </ChartLoader>
    </div>
  )
}
